import React, { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { AdvanceFilters, TooltipButton } from 'sonata-react-components';

import { hasActiveAdvanceFilters } from '@/helpers/advanceFilters/advanceFiltersNormalizer.helper';
import { SearchByFiltersButton } from './SearchByFilters';

import type { SearchByFilterEntry } from './SearchByFilters';

import './listFilters.scss';

type Props = {
  /** Extra controls rendered to the left of the advanced filters button (e.g. column visibility). */
  extraActions?: React.ReactNode;
  forceResyncKey?: number;
  initialFilters?: Record<string, any>;
  leftFilters: React.ComponentProps<typeof AdvanceFilters>['leftFilters'];
  prefixId: string;
  searchByFilters?: SearchByFilterEntry[];
  onFilter: (filters: Record<string, any>) => void;
  onShowFiltersClick?: () => void;
  onReset?: () => void;
};

type State = {
  isResetButtonDisabled: boolean;
  resetKey: number;
};

export default function ListFilters({
  extraActions,
  forceResyncKey,
  initialFilters,
  leftFilters,
  prefixId,
  searchByFilters,
  onFilter,
  onShowFiltersClick,
  onReset
}: Props) {
  const { formatMessage } = useIntl();
  const [{ isResetButtonDisabled, resetKey }, setState] = useState<State>({
    isResetButtonDisabled: true,
    resetKey: 0
  });

  // TODO: remove this logic when https://taptapnetworks.atlassian.net/browse/SWC-178 is merged
  const isJustReset = useRef(false);
  useEffect(() => {
    if (isJustReset.current) {
      setState(prevState => ({
        ...prevState,
        resetKey: prevState.resetKey + 1
      }));
      isJustReset.current = false;
    }

    setState(prevState => ({
      ...prevState,
      isResetButtonDisabled: !hasActiveAdvanceFilters(initialFilters?.advanceFilters, initialFilters?.text)
    }));
  }, [initialFilters]);

  const handleFilter = (filters: Record<string, any>) => {
    onFilter(filters);
    setState(prevState => ({
      ...prevState,
      activeFilters: filters,
      isResetButtonDisabled: !hasActiveAdvanceFilters(initialFilters?.advanceFilters, initialFilters?.text)
    }));
  };

  const handleReset = () => {
    isJustReset.current = true;
    onReset?.();
  };

  return (
    <div className="campaignsListsFilters">
      <AdvanceFilters
        className="campaignsListsFilters__advanceFilter"
        initialState={initialFilters}
        key={forceResyncKey}
        leftFilters={leftFilters}
        onChange={handleFilter}
        resetKey={resetKey}
      />
      <div className="campaignsListsFilters__actions">
        {extraActions}
        {onShowFiltersClick ? (
          <SearchByFiltersButton filters={searchByFilters} onClick={onShowFiltersClick} prefixId={prefixId} />
        ) : null}
        <TooltipButton
          buttonConfig={{
            buttonDisabled: isResetButtonDisabled,
            buttonOnClick: handleReset,
            buttonShape: 'link',
            buttonType: 'secondary'
          }}
          icon="fa-regular fa-trash"
          id="campaignPlans_filters_reset"
          isButton
          tooltipConfig={{
            tooltipEvent: 'mouseenter',
            tooltipPlace: 'top'
          }}
        >
          {formatMessage({
            defaultMessage: 'Reset filters',
            id: 'campaignPlans.filters.reset.tooltip'
          })}
        </TooltipButton>
      </div>
    </div>
  );
}
